import { useEffect, useRef, useState } from 'react';
import { istTodayIso } from './helpers';
import {
  fetchBoardingNotice, noticeLead, noticeState, noticeStorageKey, readDismissed, writeDismissed,
} from '../../lib/boardingNotice';

// Notice strip for a scheduled boarding-point change, at the very top of
// every screen. In normal flow — it pushes the page down and never overlays
// it (no modal, no sticky, no interstitial). Rendered only when
// boarding_point_changes holds a change inside the notice window
// (src/lib/boardingNotice.js), and gone again a week after the change.
//
// Loads after the screen has painted (next tick, like the internal app's
// availability strip) and fails silent: no query result, no strip. While it
// is up, its height is published as --ag-notice-h on <html> so the page
// shell below can subtract it and every screen still fits the viewport.
export default function BoardingNotice() {
  const [notice, setNotice] = useState(null); // { key, lead, point, mapsUrl }
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Next tick so the routed screen paints first, and so StrictMode's
    // dev-only double mount cancels the first run before it queries.
    const timer = setTimeout(() => {
      const today = istTodayIso();
      fetchBoardingNotice(today)
        .then((row) => {
          if (cancelled || !row) return;
          const state = noticeState(row.effective_from, today);
          if (!state) return;
          const key = noticeStorageKey(row.effective_from);
          if (readDismissed(key)) return;
          setNotice({
            key,
            lead: noticeLead(state, row.effective_from),
            point: row.boarding_point,
            mapsUrl: String(row.boarding_maps_url || '').trim(),
          });
        })
        .catch(() => {});
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!notice || !el) {
      root.style.removeProperty('--ag-notice-h');
      return undefined;
    }
    const measure = () => root.style.setProperty('--ag-notice-h', `${el.offsetHeight}px`);
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(el);
    else window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', measure);
      root.style.removeProperty('--ag-notice-h');
    };
  }, [notice]);

  if (!notice) return null;

  const dismiss = () => {
    writeDismissed(notice.key);
    setNotice(null);
  };

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Boarding point notice"
      className="w-full bg-[#0D1E35] border-b border-[rgba(212,168,67,0.35)] motion-safe:animate-[fadeInUp_0.5s_cubic-bezier(0.16,1,0.3,1)_both]"
    >
      <div className="mx-auto w-full max-w-5xl flex items-start gap-3 px-4 py-2.5 sm:items-center">
        <p className="flex-1 min-w-0 font-dm-sans text-[13px] font-light leading-snug text-white/85">
          <span className="block sm:inline font-medium text-[#D4A843] sm:mr-1.5">{notice.lead}</span>
          <span className="break-words">{notice.point}</span>
          {notice.mapsUrl && (
            <>
              <span className="text-white/35" aria-hidden="true">{' · '}</span>
              <a
                href={notice.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap text-[#C9A84C] underline underline-offset-2 hover:text-[#B8973E] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] rounded-sm"
              >
                View on map
              </a>
            </>
          )}
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss notice"
          className="shrink-0 -mr-1 -my-1 w-8 h-8 flex items-center justify-center rounded-full text-white/55 hover:text-white hover:bg-white/[0.06] active:scale-[0.94] transition-[color,background-color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)]"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
